// UserPreferencesForm.jsx
import React, { useEffect, useState, useContext, use } from 'react';

import Button from '@frolight/components/Button';
import SnackbarContext from '@frolight/context/SnackbarProvider';
import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import useUserPreferences from '@frolight/hooks/useUserPreferences';
import * as userService from '@frolight/services/api/userService';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';
import i18next from '@frolight/i18n';
import useLocalizationUtils from '@frolight/hooks/useLocalizationUtils';
import RadioGroup from '@frolight/components/RadioGroup';


const UserPreferencesForm = () => {
  const { lftext } = useLocalizedFormattedText();

  const TEMPERATURE_UNITS = [
    { value: 'celsius', label: lftext('pages.user_preferences.celsius') },
    { value: 'fahrenheit', label: lftext('pages.user_preferences.fahrenheit') },
  ];


  const { changeLocale } = useLocalizationUtils()
  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();
  const { show: showSnackbar } = useContext(SnackbarContext);
  const { userPreferences, setUserPreferences } = useUserPreferences();

  const [availableLocales, setavailableLocales] = useState([]);
  const [formDataTemperatureUnit, setFormDataTemperatureUnit] = useState(null);
  const [formDataLocale, setFormDataLocale] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const fetchLocales = async () => {
      try {
        const supportedLocales = await i18next.languages;
        setavailableLocales(supportedLocales.map(l => ({ value: l, label: lftext(`locale.${l}`) })));
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    }
    fetchLocales();
  }, []);

  useEffect(() => {
    if (formDataTemperatureUnit || formDataLocale) {
      setFormDirty(
        formDataTemperatureUnit !== userPreferences.temperatureUnit ||
        formDataLocale !== userPreferences.locale
      );
    }
  }, [formDataTemperatureUnit, formDataLocale, userPreferences.temperatureUnit, userPreferences.locale]);
  

  useEffect(() => {
    if (userPreferences.temperatureUnit) {
      setFormDataTemperatureUnit(userPreferences.temperatureUnit);
    }
    if (userPreferences.locale) {
      setFormDataLocale(userPreferences.locale);
    }
  }, [userPreferences.locale, userPreferences.temperatureUnit, availableLocales]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const changesObject = {};
      if (
        formDataTemperatureUnit &&
        formDataTemperatureUnit !== userPreferences.temperatureUnit
      ) {
        changesObject.temperatureUnit = formDataTemperatureUnit;
      }
      if (formDataLocale && formDataLocale !== userPreferences.locale) {
        changesObject.locale = formDataLocale;
        changeLocale(formDataLocale);
      }

      const abortController = new AbortController();
      const patchUserResponse = await authenticatedAPIServiceCall(userService.patchPreferences, {
        abortController,
        opts: changesObject,
      });

      if (patchUserResponse && patchUserResponse.data === 1) {
        showSnackbar(lftext('pages.user_preferences.preferences_updated'));
        setUserPreferences(prev => ({
          ...prev,
          ...changesObject,
        }));
        setFormDirty(false);
      } else {
        setSaveError(new Error('Error while updating user preferences.'));
      }
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderError = () => (
    <p className="feedback_message feedback_message--error">
      {lftext('pages.user_preferences.contact_rep_error')}
    </p>
  );

  if (!userPreferences) {
    return <span>{lftext('common.loading')}</span>;
  }

  return (
    <form className="form" onSubmit={onSubmit} method="post">
      {saveError && renderError()}
      <div className="form__grid">
        <div className="form__grid_item form__grid_item--medium">
          <div className="form__field">
            <RadioGroup
            label={lftext('pages.user_preferences.language_label')}
            name="language"
            options={availableLocales}
            onChange={setFormDataLocale}
            disabled={saving}
            FormDataValue={formDataLocale}
            UserPreferencesValue={userPreferences.locale}
          />
          </div>
        </div>
        <div className="form__grid_item form__grid_item--medium">
          <div className="form__field">
            <RadioGroup
              label={lftext('pages.user_preferences.temperature_label')}
              name="temperature_unit"
              options={TEMPERATURE_UNITS}
              onChange={setFormDataTemperatureUnit}
              disabled={saving}
              FormDataValue={formDataTemperatureUnit}
              UserPreferencesValue={userPreferences.temperatureUnit}
            />
          </div>
        </div>
      </div>
      <div className="form__actions">
        <Button
          type="submit"
          className="button"
          label={lftext('pages.user_preferences.save_button')}
          priority="primary"
          disabled={saving || !formDirty}
        />
      </div>
    </form>
  );
};

export default UserPreferencesForm;
